import { useEffect } from 'react';
import { Bundle } from '@root/src/types';
import { PromotionalPricingForBundle, usePromotionalPricing, usePurchaseOptionSelection } from 'contexts/AppContext';
import { ItemPrice } from '@root/src/partials/products/Price';
import FormRadioItem from 'components/FormRadioItem';
import { getVariantsOfBundle } from 'utils/utils';

export type RentOrBuyOptionsType = 'Rent' | 'Buy';

export type BundleRadioPricesProps = {
  bundle: Bundle;
};

const BundleRadioOptions = ({ bundle }: BundleRadioPricesProps) => {
  const { selection, actions } = usePurchaseOptionSelection();
  const promotionalPricing = usePromotionalPricing(bundle) as PromotionalPricingForBundle;

  const isSelected = (value: string) => selection === value;
  const { rentalBundle, retailBundle } = getVariantsOfBundle(bundle);

  const showRentalPrice = !!rentalBundle;
  const showRetailPrice = !!retailBundle;

  const baseRentalPrice = rentalBundle?.cost ?? '';
  const baseRetailPrice = retailBundle?.cost ?? '';

  const isSinglePrice = (!showRentalPrice && showRetailPrice) || (showRentalPrice && !showRetailPrice);

  const discountedRentalValue = promotionalPricing?.rentalValue;
  const discountedRetailValue = promotionalPricing?.retailValue;

  const hasSelectedDiscountCandidate = isSelected('Rent') ? !!discountedRentalValue : !!discountedRetailValue;

  useEffect(() => {
    //For only one purchase options.
    if (isSinglePrice) {
      if (showRetailPrice) {
        actions.onPurchaseOptionChange('Buy');
      } else {
        actions.onPurchaseOptionChange('Rent');
      }
    }
  }, [selection, isSinglePrice, showRetailPrice]);

  return (
    <div className="space-y-8">
      {showRentalPrice && (
        <div className="flex items-end justify-between">
          <div className="relative inline-block text-left">
            {!isSinglePrice ? (
              <FormRadioItem
                className="!mx-0 -mb-8 !gap-8"
                label="Rent"
                checked={isSelected('Rent')}
                onClick={() => {
                  actions.onPurchaseOptionChange('Rent');
                }}
              />
            ) : (
              <p className="mr-8 leading-3">Rent</p>
            )}
          </div>

          <div className="h-2 flex-1 bg-leader bg-bottom bg-repeat-x" />

          <p
            className={`text-h4 leading-3 ${
              !isSelected('Rent') || discountedRentalValue ? 'text-gray' : 'text-gray-dark'
            } ml-8`}
          >
            {isSelected('Rent') ? (
              <>
                <ItemPrice price={baseRentalPrice} discountedPrice={discountedRentalValue} />
                {discountedRentalValue && (
                  <span className="font-condensed-light normal-case text-red-light sm-only:hidden">
                    {' '}
                    with code <span className="promo-code">{promotionalPricing?.promoCode}</span>
                  </span>
                )}
              </>
            ) : (
              <span className={`font-bold ${discountedRentalValue ? 'line-through' : ''}`}>
                <ItemPrice price={baseRentalPrice} />
              </span>
            )}
          </p>
        </div>
      )}

      {showRetailPrice && (
        <div className="flex items-end justify-between">
          <div className="relative inline-block text-left">
            {!isSinglePrice ? (
              <FormRadioItem
                className="!mx-0 -mb-8 !gap-8"
                label="Buy"
                checked={isSelected('Buy')}
                onClick={() => {
                  actions.onPurchaseOptionChange('Buy');
                }}
              />
            ) : (
              <p className="mr-8 leading-3">Buy</p>
            )}
          </div>

          <div className="h-2 flex-1 bg-leader bg-bottom bg-repeat-x" />

          <p
            className={`text-h4 leading-3 ${
              !isSelected('Buy') || discountedRetailValue ? 'text-gray' : 'text-gray-dark'
            } ml-8`}
          >
            {isSelected('Buy') ? (
              <>
                <ItemPrice price={baseRetailPrice} discountedPrice={discountedRetailValue} />
                {discountedRetailValue && (
                  <span className="font-condensed-light normal-case text-red-light sm-only:hidden">
                    {' '}
                    with code <span className="promo-code">{promotionalPricing?.promoCode}</span>
                  </span>
                )}
              </>
            ) : (
              <span className={`font-bold ${discountedRetailValue ? 'line-through' : ''}`}>
                <ItemPrice price={baseRetailPrice} />
              </span>
            )}
          </p>
        </div>
      )}

      {hasSelectedDiscountCandidate && (
        <div className="!mt-16 hidden justify-end pl-8 font-condensed-light text-red-light sm-only:flex">
          With code <span className="promo-code mx-4">{promotionalPricing?.promoCode}</span> at checkout
        </div>
      )}
    </div>
  );
};

export default BundleRadioOptions;
